import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps extends React.PropsWithChildren {
  open: boolean
  close: () => void
  showClose?: boolean
  overlayClassName?: string
  modalClassName?: string
  title?: string
  showHeader?: boolean
  footer?: React.ReactNode
  widthClass?: string
}

const Modal = ({
  open,
  close,
  children,
  showClose = true,
  overlayClassName = '',
  modalClassName = '',
  title,
  showHeader = !!title,
  footer,
  widthClass = 'w-[calc(100%-32px)] sm:max-w-md md:max-w-[640px]',
}: ModalProps) => {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = original
    }
  }, [open, close])

  if (!open) return null

  return createPortal(
    <div
      className={`fixed inset-0 flex justify-center items-center py-6 sm:py-12 bg-gray-300/60 overflow-auto ${overlayClassName}`}
      style={{ zIndex: 998 }}
      onClick={close}
    >
      <div
        className={`relative rounded-lg bg-white p-3 sm:p-4 md:p-6 max-w-full max-h-[90vh] ${widthClass} ${modalClassName}`}
        style={{ zIndex: 999 }}
        onClick={(e) => e.stopPropagation()}
      >
        {showHeader && (
          <div className="flex items-start justify-between mb-3 sm:mb-4">
            {title ? <h3 className="text-base sm:text-lg font-semibold">{title}</h3> : null}
            {showClose && (
              <button onClick={close} aria-label="close" className="text-gray-400 hover:text-gray-700 flex-shrink-0 ml-2">
                ✕
              </button>
            )}
          </div>
        )}

        <div className="overflow-y-auto">{children}</div>

        {footer && <div className="mt-4 sm:mt-6">{footer}</div>}
      </div>
    </div>,
    document.body
  )
}

export default Modal
